import Modal from "components/modal/Modal";
import { useIsMobile } from "hooks/use-is-mobile";
import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { hasAccessToInvoiceReport, logoutUser } from "common/MetaActions";
import Admin from "views/admin/containers/Admin";
import AllCustomers from "views/customers/allCustomers/containers/AllCustomers";
import Customers from "views/customers/Customers";
import { Documents } from "views/customers/documents/containers/Documents";
import MyCustomers from "views/customers/myCustomers/containers/MyCustomers";
import CustomerSettings from "views/customers/settings/containers/CustomerSettings";
import Dashboard from "views/dashboard/containers/Dashboard";
import DocumentDelivery from "views/delivery/containers/DocumentDelivery";
import { DocumentDeliveryTable } from "views/delivery/containers/DocumentDeliveryTable";
import { DocumentDeliveryTableWithOptions } from "views/delivery/containers/DocumentDeliveryTableWithOptions";
import { DocumentDeliveryUpload } from "views/delivery/containers/DocumentDeliveryUpload";
import DocumentArchive from "views/documents/containers/DocumentArchive";
import Footer from "views/footer/Footer";
import Header from "views/header/Header";
import InboxPreview from "views/inbox/components/Inbox/inboxPreview";
import InboxSidebar from "views/inbox/components/Inbox/InboxSidebar";
import MessageEditor from "views/inbox/components/Inbox/main/messageEditor";
import Inbox from "views/inbox/containers/inbox/inbox";
import Inventory from "views/inventory/containers/Inventory";
import Invoices from "views/invoices/containers/Invoices";
import CustomerInformationHeader from "views/layout/components/CustomerInformationHeader";
import CustomerServerSettings from "views/settings/containers/CustomerServerSettings";
import Wizard from "views/wizard/containers/Wizard";

import { PrimaryButton } from "components/button/Button";
import { useRootStore } from "store";
import "./Layout.scss";
import ProtectedRoute from "helpers/inbox/protectedRoute";

export default function Layout() {
  const {
    fetchCustomers,
    fetchCustomer,
    myCustomers,
    workingCustomer: customer
  } = useRootStore((state) => state.customers);
  const { unfilteredCustomers: customerList } = myCustomers;
  const location = useLocation();
  const isMobile = useIsMobile();
  const { workingCustomerId } = useRootStore((state) => state.global);
  const { user, inventory } = useRootStore((state) => state);
  const { uploadedDocuments, isLoadingUploadedDocuments } = useRootStore(
    (state) => state.delivery
  );
  const { getInventories } = inventory;
  const { fetchDashboard } = useRootStore((state) => state.dashboard);
  const { getSettings } = useRootStore((state) => state.customerServerSettings);

  const { customerNumber } = customer;
  const isExternalUser = user.userInfo && user.userInfo.type === "External";
  const { sessionExpired, setSessionExpired } = user;

  const mobileInboxRoutes = (
    <>
      <Route
        path="/inbox"
        element={
          <ProtectedRoute
            redirectTo="/inbox/my"
            accessCondition={(inboxType) =>
              !(isExternalUser && inboxType === "all")
            }
          >
            <Inbox />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="my" replace />} />
        <Route path=":inboxType" element={<InboxSidebar />} />
        <Route path=":inboxType/thread/:threadId" element={<InboxPreview />} />
        <Route
          path=":inboxType/messages/:messageId"
          element={<InboxPreview />}
        />
        <Route path=":inboxType/new" element={<MessageEditor />} />
      </Route>
    </>
  );

  const desktopInboxRoutes = (
    <Route
      path="/inbox"
      element={
        <ProtectedRoute
          redirectTo="/inbox/my"
          accessCondition={(inboxType) =>
            !(isExternalUser && inboxType === "all")
          }
        >
          <Inbox />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="my" replace />} />
      <Route path=":inboxType">
        <Route path="thread/:threadId" element={<InboxPreview />} />

        <Route path="messages/:messageId" element={<InboxPreview />} />

        <Route path="new" element={<MessageEditor />} />
        <Route index element={<InboxPreview />} />
      </Route>
    </Route>
  );

  useEffect(() => {
    if (isExternalUser) {
      getSettings();
    }
    if (
      user.isAuthenticated &&
      !customer.isLoading &&
      !customer.customerId &&
      customerList.length === 0
    ) {
      fetchCustomers(isExternalUser);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (workingCustomerId && customerNumber !== global.workingCustomerId) {
      fetchCustomer(workingCustomerId);
      fetchDashboard(workingCustomerId);
      if (isExternalUser) {
        getInventories(workingCustomerId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workingCustomerId,
    isExternalUser,
    fetchCustomer,
    getInventories,
    fetchDashboard
  ]);

  const baseClassName =
    location.pathname.includes("inventory") ||
    (location.pathname.includes("invoices") && hasAccessToInvoiceReport())
      ? "invoiceWrapper"
      : "contentWrapper";
  // When session expires, for now, we just want to close the Session-expires modal
  // and not redirect to logout, because we want to allow the user to copy information that they were working on.
  const handleSessionExpired = () => {
    setSessionExpired(false);
  };

  const contentBoxedClassName = location.pathname.includes("inbox")
    ? "content-boxed content-boxed-inbox"
    : "content-boxed";

  const handleLogout = () => {
    logoutUser();
    window.location.href = "/";
  };

  const shouldShowCustomerInfo =
    !location.pathname.startsWith("/customers/my") &&
    !location.pathname.startsWith("/customers/all") &&
    !location.pathname.startsWith("/admin");

  return (
    <div className="main-layout">
      <Header />
      {shouldShowCustomerInfo && (
        <div className="customer-information-header-container">
          <CustomerInformationHeader />
        </div>
      )}

      <div className="content-with-footer" id="content-with-footer">
        <div id="main-content" className={baseClassName}>
          <div className={contentBoxedClassName}>
            <Routes>
              <Route path="/customers" element={<Customers />}>
                <Route path="all" element={<AllCustomers />} />
                {hasAccessToInvoiceReport() && (
                  <Route path="invoices" element={<Invoices />} />
                )}
                <Route path="my" element={<MyCustomers />} />
                <Route path=":customerId/dashboard" element={<Dashboard />} />
                <Route path=":customerId/inventory" element={<Inventory />} />
                <Route
                  path=":customerId/delivery"
                  element={<DocumentDelivery />}
                />
                <Route path=":customerId/inbox" element={<Inbox />} />
                <Route
                  path=":customerId/settings"
                  element={<CustomerSettings />}
                />
                <Route path=":customerId" element={<Customers />} />
                <Route index element={<Navigate to="my" replace />} />
              </Route>

              {/* Inbox */}
              {isMobile ? mobileInboxRoutes : desktopInboxRoutes}

              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/customersettings" element={<CustomerSettings />} />
              <Route path="/settings" element={<CustomerServerSettings />} />
              <Route path="/delivery" element={<DocumentDelivery />}>
                <Route
                  index
                  element={
                    <Navigate to={!isExternalUser ? "new" : "upload"} replace />
                  }
                />

                <Route path="upload" element={<DocumentDeliveryUpload />} />
                <Route path="uploaded" element={<DocumentDeliveryTable />} />
                <Route
                  path="handled"
                  element={
                    <DocumentDeliveryTableWithOptions
                      files={uploadedDocuments.filter((d) => d.handled)}
                      isLoading={isLoadingUploadedDocuments}
                      title="Hanterade"
                    />
                  }
                />
                <Route
                  path="new"
                  element={
                    <DocumentDeliveryTableWithOptions
                      files={uploadedDocuments.filter((d) => !d.handled)}
                      isLoading={isLoadingUploadedDocuments}
                      title="Nya leveranser"
                    />
                  }
                />
              </Route>
              <Route path="/archive" element={<DocumentArchive />} />
              <Route path="/wizard/:id" element={<Wizard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route
                path="*"
                element={
                  <Navigate to={isExternalUser ? "/dashboard" : "/customers"} />
                }
              />
            </Routes>
          </div>
        </div>
        <Footer />
      </div>

      {sessionExpired && (
        <Modal
          size="xs"
          modalTitle="Session expired"
          onDismiss={handleSessionExpired}
        >
          <div className="modalText">
            <h5>
              Din session har gått ut. För att starta en ny session behöver du
              logga in på nytt.
            </h5>
            <div className="d-flex justify-content-end">
              <PrimaryButton size="md" onClick={handleLogout} type="submit">
                Logga ut
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
